import { FC, useEffect, useMemo } from 'react'
import { Box, CircularProgress, Typography, IconButton } from '@mui/material'

import { useNavigate, useParams } from 'react-router-dom'
import { useOpenOffersList, useOpenTermsOfUse } from 'services/openOffers/openOffers.hooks'
import OpenOffersLayout from 'layouts/OpenOffers'
import { useIntl } from 'react-intl'
import OfferDetails from 'components/Offer/OfferDetailsView/OfferDetails'
import { ArrowBackIosNew } from '@mui/icons-material'
import TermsOfUseForm from 'components/TermsOfUse/TermsOfUseForm'

const OpenOfferPage: FC = () => {
  const { formatMessage } = useIntl()
  const { token, id } = useParams()
  const { data: offerList = [], isLoading: isOffersListLoading } = useOpenOffersList(
    token as string
  )
  const {
    hasUserConsented,
    isLoadingTermsOfUse,
    termsOfUse,
    isLoading: isTermsOfUseLoading,
    mutateAsyncTermsOfUse,
    refetch: refetchTermsOfUse
  } = useOpenTermsOfUse(token as string)
  const navigate = useNavigate()

  const isLoading = useMemo(
    () => isOffersListLoading || isLoadingTermsOfUse,
    [isOffersListLoading, isLoadingTermsOfUse]
  )

  const offer = useMemo(() => {
    return offerList.find((offer) => offer.id === Number(id))
  }, [offerList, id])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  if (isLoading) {
    return (
      <OpenOffersLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </OpenOffersLayout>
    )
  }

  if (!hasUserConsented) {
    return (
      <OpenOffersLayout>
        <TermsOfUseForm
          isLoading={isTermsOfUseLoading}
          onAccept={async () => {
            await mutateAsyncTermsOfUse(termsOfUse?.id)
            await refetchTermsOfUse()
          }}
          onExit={() => navigate('/')}
          termsOfUse={termsOfUse}
        />
      </OpenOffersLayout>
    )
  }

  if (offerList.length <= 0 || !offer) {
    return (
      <OpenOffersLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant='h5' fontWeight={500}>
            {formatMessage({
              id: 'widgetOffer.noActiveOffers',
              defaultMessage: 'Nie masz aktywnych ofert, poproś o nową ofertę'
            })}
          </Typography>
        </Box>
      </OpenOffersLayout>
    )
  }

  return (
    <OpenOffersLayout>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          width: '100%',
          maxWidth: '530px',
          mb: 2
        }}>
        <IconButton onClick={() => navigate(`/oferta/${token}`)}>
          <ArrowBackIosNew />
        </IconButton>
        <Typography sx={{ mx: 1, flex: 1, fontWeight: 500 }}>
          {formatMessage({
            id: 'common.goBack',
            defaultMessage: 'Wróć'
          })}
        </Typography>
      </Box>
      <OfferDetails offer={offer} />
    </OpenOffersLayout>
  )
}

export default OpenOfferPage
